import { SphereSupportLink } from "@components/common/sphere-support-link";
import { ContactSupportLocations } from "@custom-types/types";
import { Banner, Text } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useRouteMatch } from "@hooks/use-route-match";
import { Box} from "@mui/material";
import { appStateSelector } from "@store/app/app-selector";
import { topPriorityXgSubscriptionSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { getSubscriptionRemainingDays } from "@utils/subscription-utils";
import { useState } from "react";

/** Default remaining days to start showing the banner */
const DEFAULT_DAYS = 7;

interface Props {
  /** threshold days to start showing the alert banner */
  alertThresholdDays?: number;
}

/** Banner to show when a subscription almost ended in {days} days */
export function SubscriptionExpirationBanner({
  alertThresholdDays = DEFAULT_DAYS,
}: Props): JSX.Element | null {
  const routeIdentifier = useRouteMatch();

  const subscription = useAppSelector(topPriorityXgSubscriptionSelector);
  const [isDismissed, setIsDismissed] = useState(false);
  const { canViewSubscriptionDetails } = useHasUserValidRoleCompanyLevel();

  // TODO: Remove beta testing: https://faro01.atlassian.net/browse/ST-3626
  const { isBetaTestingEnabled } = useAppSelector(appStateSelector);

  /** The remaining days until subscription expires */
  const remainingDays =
    getSubscriptionRemainingDays(subscription) ?? alertThresholdDays + 1;

  /**
   * Determines if the banner should be shown
   * It should be hidden on the subscription expired page
   */
  const shouldShowBanner =
    routeIdentifier !== "subscriptionExpired" &&
    isBetaTestingEnabled &&
    remainingDays <= alertThresholdDays &&
    !isDismissed &&
    canViewSubscriptionDetails;

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Box data-testid="expiration-subscription-banner">
      <Banner
        variant="warning"
        onClose={() => setIsDismissed(true)}
        title="Subscription ending soon"
      >
        <Text variant="bodyM">
          Your subscription will expire in {remainingDays} days. Renew now to
          avoid interruptions, or contact{" "}
          <SphereSupportLink location={ContactSupportLocations.subscriptionExpiredBanner} />{" "}
          for further information.
        </Text>
      </Banner>
    </Box>
  );
}
